import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarDays, X } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangePickerProps {
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  className?: string;
}

function toDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  try { return parseISO(iso); } catch { return undefined; }
}

function toISO(d: Date | undefined): string {
  if (!d) return '';
  return format(d, 'yyyy-MM-dd');
}

function formatLabel(from: string, to: string): string {
  const f = toDate(from);
  const t = toDate(to);
  if (f && t) return `${format(f, 'MMM d')} – ${format(t, 'MMM d, yyyy')}`;
  if (f) return `From ${format(f, 'MMM d, yyyy')}`;
  return 'Select date range';
}

export function DateRangePicker({
  dateFrom, dateTo, onDateFromChange, onDateToChange, className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  const selected: DateRange | undefined =
    toDate(dateFrom) || toDate(dateTo)
      ? { from: toDate(dateFrom), to: toDate(dateTo) }
      : undefined;

  const hasRange = !!(dateFrom || dateTo);

  function handleSelect(range: DateRange | undefined) {
    onDateFromChange(toISO(range?.from));
    onDateToChange(toISO(range?.to));
    if (range?.from && range?.to) setOpen(false);
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    onDateFromChange('');
    onDateToChange('');
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-10 justify-start gap-2 text-left font-normal',
            !hasRange && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarDays className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">{formatLabel(dateFrom, dateTo)}</span>
          {hasRange && (
            <span
              role="button"
              aria-label="Clear dates"
              onClick={handleClear}
              className="ml-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={selected}
          onSelect={handleSelect}
          numberOfMonths={1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
